import React, { forwardRef, useCallback, useEffect, useId, useRef } from 'react';
import clsx from 'clsx';
import { ATextAreaProps } from './ATextArea.types';
import * as Styled from './ATextArea.styles';

const COMPONENT_NAME = 'a-mi-chat-components-ATextArea';
const TEST_ID = 'mi-chat-components-ATextArea';

export const ATextArea = forwardRef<HTMLTextAreaElement, ATextAreaProps>(
  ({ text, label, className, onChange, id: idProp, placeholder, ...rest }, ref) => {
    const generatedId = useId();
    const textareaId = idProp ?? generatedId;
    const placeholderText = text ?? placeholder;
    const labelText = label ?? placeholderText;
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const resizeToContent = useCallback((el: HTMLTextAreaElement | null) => {
      if (!el) return;
      const computed = window.getComputedStyle(el);
      const minHeight = parseFloat(computed.minHeight) || 24;

      el.style.height = 'auto';
      const nextHeight = Math.max(el.scrollHeight, minHeight);
      el.style.height = `${nextHeight}px`;
      el.style.overflowY = 'hidden';
    }, []);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        resizeToContent(node);
      },
      [ref, resizeToContent]
    );

    useEffect(() => {
      resizeToContent(internalRef.current);
    }, [resizeToContent, rest.value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      resizeToContent(e.currentTarget);
      onChange?.(e);
    };

    return (
      <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={clsx(className)}>
        {labelText ? (
          <label htmlFor={textareaId} className="sr-only">
            {labelText}
          </label>
        ) : null}
        <div className="m-input-field">
          <Styled.Textarea
            ref={setRefs}
            className={clsx('t-font-m')}
            rows={1}
            onChange={handleChange}
            {...rest}
            id={textareaId}
            placeholder={placeholderText}
          />
        </div>
      </Styled.Wrapper>
    );
  }
);

ATextArea.displayName = 'ATextArea';

export { TEST_ID as A_TEXT_AREA_TEST_ID };
